/**
 * SCWT-PM-V2 Phase 5 — Landlord magic-link login.
 * Route: /landlord/login
 */
import { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

export default function LandlordLogin() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/landlord/auth/request-link", { email });
      setSent(true);
    } catch { /* generic response */ setSent(true); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
            AWARE · Landlord portal
          </div>
          <h1 className="mt-3 text-2xl font-bold text-slate-900">Sign in to your portal</h1>
          <p className="mt-2 text-sm text-slate-600">
            Enter your email and we'll send you a single-use sign-in link.
            Links expire in 30 minutes.
          </p>
          {sent ? (
            <div data-testid="landlord-login-sent" className="mt-6 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
              <div className="flex items-start gap-2 text-emerald-800 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                <span>If an account exists for that email, a sign-in link has been sent.</span>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold text-slate-700 mb-1">Email</span>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute top-2.5 left-3 text-slate-400" />
                  <input data-testid="landlord-login-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          type="email" required
                          className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                </div>
              </label>
              <button data-testid="landlord-login-submit"
                       type="submit" disabled={busy}
                       className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-2.5 text-sm w-full justify-center">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Send sign-in link
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
